import { useEffect, useState } from 'react'
import { ArrowLeft } from 'react-feather'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import * as S from './RemyGuide.styles'
import { fetchRemyGuide } from './remySessions'

// Guide links point outside Voyage (Slack, docs); keep the panel open.
const guideComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
}

interface RemyGuideProps {
  onClose: () => void
}

// The Remy user guide, fetched from the gateway and shown as an overlay
// covering the chat. Rendered on demand so the markdown is only fetched when
// the user first opens it.
export const RemyGuide = ({ onClose }: RemyGuideProps) => {
  const [markdown, setMarkdown] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchRemyGuide()
      .then(text => {
        if (!cancelled) setMarkdown(text)
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Couldn't load the guide")
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <S.Overlay role="dialog" aria-modal="true" aria-label="User Guide">
      <S.Header>
        <S.Title>User Guide</S.Title>
        <S.BackButton type="button" onClick={onClose}>
          <ArrowLeft size={14} />
          Back to chat
        </S.BackButton>
      </S.Header>
      <S.Body>
        {!markdown && !error && <S.Status>Loading the guide…</S.Status>}
        {error && <S.Status role="alert">{error}</S.Status>}
        {markdown && (
          <S.MarkdownContent>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={guideComponents}>
              {markdown}
            </ReactMarkdown>
          </S.MarkdownContent>
        )}
      </S.Body>
    </S.Overlay>
  )
}
